import React, { useCallback, useEffect, useRef, useState } from "react";
import { Drawer } from "antd";
import type { DrawerProps } from "antd";
import type { DrawerSemanticStyles } from "antd/lib/drawer/DrawerPanel";
import DrawerHeader from "@/components/UI/DrawerHeader";

type DrawerWidth = number | string;

interface ResizableDrawerProps extends Omit<DrawerProps, "width" | "size"> {
  /** Uncontrolled: initial width. Ignored when `width` is provided. */
  defaultWidth?: DrawerWidth;
  /** Controlled: width driven by the parent (e.g. for a toggle button). */
  width?: number;
  /** Called on every drag update so the parent can sync its own state. */
  onWidthChange?: (width: number) => void;
  minWidth?: number;
  maxWidth?: number;
  headerTitle?: React.ReactNode;
  headersArray?: Array<{ key: string; value: React.ReactNode }>;
  headerActions?: React.ReactNode;
  sizeToggle?: {
    collapsedWidth: DrawerWidth;
    expandedWidth: DrawerWidth;
    initialWidth?: DrawerWidth;
  };
}

/**
 * Drop-in replacement for antd Drawer that adds continuous drag-to-resize
 * via a handle on the left edge.
 *
 * Uncontrolled usage (drag only):
 *   <ResizableDrawer defaultWidth={654} ...>
 *
 * Controlled usage (drag + external toggle):
 *   <ResizableDrawer width={width} onWidthChange={setWidth} ...>
 *   // parent toggles width freely; drag calls onWidthChange to keep in sync
 */
export default function ResizableDrawer({
  children,
  defaultWidth = "50%",
  width: controlledWidth,
  onWidthChange,
  minWidth = 400,
  maxWidth = 1400,
  headerTitle,
  headersArray,
  headerActions,
  sizeToggle,
  styles: drawerStyles,
  ...drawerProps
}: ResizableDrawerProps) {
  const [internalWidth, setInternalWidth] = useState(
    sizeToggle?.initialWidth ?? defaultWidth,
  );
  const width = controlledWidth ?? internalWidth;
  const resolvedWidth = resolveWidth(width);

  const isResizing = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);
  const handleMouseMoveRef = useRef<((event: MouseEvent) => void) | null>(null);
  const handleMouseUpRef = useRef<(() => void) | null>(null);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      isResizing.current = true;
      startX.current = e.clientX;
      startWidth.current = resolvedWidth;
      e.preventDefault();

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!isResizing.current) return;
        // For a right-placed drawer, dragging left increases width
        const delta = startX.current - moveEvent.clientX;
        const newWidth = Math.min(
          maxWidth,
          Math.max(minWidth, startWidth.current + delta),
        );
        setInternalWidth(newWidth);
        onWidthChange?.(newWidth);
      };

      const handleMouseUp = () => {
        isResizing.current = false;
        if (handleMouseMoveRef.current) {
          document.removeEventListener("mousemove", handleMouseMoveRef.current);
        }
        if (handleMouseUpRef.current) {
          document.removeEventListener("mouseup", handleMouseUpRef.current);
        }
        handleMouseMoveRef.current = null;
        handleMouseUpRef.current = null;
      };

      handleMouseMoveRef.current = handleMouseMove;
      handleMouseUpRef.current = handleMouseUp;
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [resolvedWidth, minWidth, maxWidth, onWidthChange],
  );

  useEffect(() => {
    return () => {
      if (handleMouseMoveRef.current) {
        document.removeEventListener("mousemove", handleMouseMoveRef.current);
      }
      if (handleMouseUpRef.current) {
        document.removeEventListener("mouseup", handleMouseUpRef.current);
      }
    };
  }, []);

  const handleToggleSize = useCallback(() => {
    if (!sizeToggle) return;

    const collapsedWidth = resolveWidth(sizeToggle.collapsedWidth);
    const nextWidth =
      resolveWidth(width) === collapsedWidth
        ? sizeToggle.expandedWidth
        : sizeToggle.collapsedWidth;

    if (controlledWidth !== undefined) {
      onWidthChange?.(resolveWidth(nextWidth));
    } else {
      setInternalWidth(nextWidth);
    }
  }, [controlledWidth, onWidthChange, sizeToggle, width]);

  return (
    <Drawer
      {...drawerProps}
      title={
        headerTitle ? (
          <DrawerHeader
            title={headerTitle}
            headersArray={headersArray}
            onChangeSize={sizeToggle ? handleToggleSize : undefined}
            onClose={
              drawerProps.onClose
                ? () => drawerProps.onClose?.({} as React.MouseEvent)
                : undefined
            }
            actions={headerActions}
          />
        ) : (
          drawerProps.title
        )
      }
      size={width}
      styles={{
        ...drawerStyles,
        body: {
          position: "relative",
          paddingLeft: 20,
          ...(typeof drawerStyles === "object"
            ? (drawerStyles as DrawerSemanticStyles).body
            : undefined),
        },
      }}
    >
      {/* Resize handle on the left edge of the drawer body */}
      <ResizeHandle onMouseDown={handleMouseDown} />
      {children}
    </Drawer>
  );
}

function resolveWidth(width: DrawerWidth): number {
  if (typeof width === "number") {
    return width;
  }

  const parsed = Number.parseFloat(width);
  if (Number.isNaN(parsed)) {
    return 654;
  }

  if (width.trim().endsWith("%")) {
    return Math.round((window.innerWidth * parsed) / 100);
  }

  if (width.trim().endsWith("vw")) {
    return Math.round((window.innerWidth * parsed) / 100);
  }

  if (width.trim().endsWith("px")) {
    return parsed;
  }

  return parsed;
}

function ResizeHandle({
  onMouseDown,
}: {
  onMouseDown: (e: React.MouseEvent) => void;
}) {
  return (
    <div
      data-testid="drawer-resize-handle"
      onMouseDown={onMouseDown}
      style={{
        position: "absolute",
        left: 0,
        top: 0,
        bottom: 0,
        width: 12,
        cursor: "ew-resize",
        zIndex: 10,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Grip dots */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            style={{
              width: 3,
              height: 3,
              borderRadius: "50%",
              backgroundColor: "#8fa3ad",
            }}
          />
        ))}
      </div>
    </div>
  );
}
